/**
 * AdminKycFormBuilderCard
 *
 * Lets the admin define what fields appear in each role's KYC form.
 * 4 role tabs (vendor / venue / professional / entertainment_pro) — each
 * with its own ordered field list. Drag-free reorder via up/down buttons
 * keeps the UX simple on touch devices.
 *
 * Saves to `PUT /api/admin/kyc-schemas/{bucket}`. Vendor-facing form
 * fetches the schema via `GET /api/kyc/form-schema` and renders dynamically.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  IdentificationCard, Plus, Trash, ArrowUp, ArrowDown,
  FloppyDisk, ArrowCounterClockwise, Spinner,
} from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BUCKET_LABELS = {
  vendor: 'Vendor',
  venue: 'Venue',
  professional: 'Professional',
  entertainment_pro: 'Entertainment Pro',
};

const blankField = () => ({
  key: '', label: '', type: 'text', required: true, placeholder: '',
  options: null, accept: null, help: '', group: '',
});

const AdminKycFormBuilderCard = () => {
  const [loading, setLoading] = useState(true);
  const [activeBucket, setActiveBucket] = useState('vendor');
  const [schemas, setSchemas] = useState({});
  const [fieldTypes, setFieldTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/kyc-schemas`,
        { withCredentials: true });
      const next = {};
      Object.keys(data.schemas).forEach(b => { next[b] = data.schemas[b].fields; });
      setSchemas(next);
      setFieldTypes(data.field_types);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load KYC schemas');
    } finally { setLoading(false); }
  };

  const currentFields = schemas[activeBucket] || [];

  const updateField = (idx, patch) => {
    setSchemas(prev => ({
      ...prev,
      [activeBucket]: prev[activeBucket].map((f, i) =>
        i === idx ? { ...f, ...patch } : f),
    }));
  };

  const moveField = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= currentFields.length) return;
    setSchemas(prev => {
      const arr = [...prev[activeBucket]];
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return { ...prev, [activeBucket]: arr };
    });
  };

  const removeField = (idx) => {
    setSchemas(prev => ({
      ...prev,
      [activeBucket]: prev[activeBucket].filter((_, i) => i !== idx),
    }));
  };

  const addField = () => {
    setSchemas(prev => ({
      ...prev,
      [activeBucket]: [...(prev[activeBucket] || []), blankField()],
    }));
  };

  const saveBucket = async () => {
    setSaving(true);
    try {
      const payload = {
        fields: currentFields.map(f => ({
          ...f,
          options: f.type === 'dropdown' || f.type === 'upload'
            ? (Array.isArray(f.options) ? f.options : (f.options || '').split(',').map(s => s.trim()).filter(Boolean))
            : null,
        })),
      };
      const { data } = await axios.put(
        `${BACKEND_URL}/api/admin/kyc-schemas/${activeBucket}`,
        payload, { withCredentials: true });
      setSchemas(prev => ({ ...prev, [activeBucket]: data.fields }));
      toast.success(`Saved ${BUCKET_LABELS[activeBucket]} KYC form (${data.fields.length} fields).`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const resetBucket = async () => {
    if (!window.confirm(`Reset ${BUCKET_LABELS[activeBucket]} to the built-in default fields? Your edits will be lost.`)) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/kyc-schemas/${activeBucket}/reset`,
        {}, { withCredentials: true });
      setSchemas(prev => ({ ...prev, [activeBucket]: data.fields }));
      toast.success('Restored built-in default schema.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed');
    } finally { setSaving(false); }
  };

  const optionsAsCsv = useMemo(() =>
    currentFields.map(f => Array.isArray(f.options) ? f.options.join(', ') : (f.options || ''))
  , [currentFields]);

  return (
    <Card className="rounded-lg border-[#E8E1D5]" data-testid="admin-kyc-form-builder-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <IdentificationCard size={20} weight="fill" className="text-brand-primary" />
          KYC Form Builder
        </CardTitle>
        <CardDescription>
          Define what fields appear in the KYC form for each role. Vendors / pros see only the fields you list here.
          Changes take effect immediately for new submissions.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-sm text-text-secondary flex items-center gap-2">
            <Spinner className="animate-spin" size={14} /> Loading schemas…
          </p>
        ) : (
          <Tabs value={activeBucket} onValueChange={setActiveBucket}>
            <TabsList className="w-full justify-start overflow-x-auto">
              {Object.keys(BUCKET_LABELS).map(b => (
                <TabsTrigger key={b} value={b} className="flex-shrink-0"
                  data-testid={`kyc-builder-tab-${b}`}>
                  {BUCKET_LABELS[b]}
                </TabsTrigger>
              ))}
            </TabsList>

            {Object.keys(BUCKET_LABELS).map(b => (
              <TabsContent key={b} value={b} className="space-y-3 mt-4">
                {(schemas[b] || []).map((field, idx) => (
                  <div key={idx} className="border border-[#E8E1D5] rounded p-3 space-y-2"
                    data-testid={`kyc-field-${b}-${idx}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-text-secondary w-6">#{idx + 1}</span>
                      <Button onClick={() => moveField(idx, -1)} variant="outline" size="sm"
                        className="h-7 w-7 p-0 rounded-none border-[#E8E1D5]"
                        data-testid={`kyc-field-up-${b}-${idx}`}>
                        <ArrowUp size={12} />
                      </Button>
                      <Button onClick={() => moveField(idx, +1)} variant="outline" size="sm"
                        className="h-7 w-7 p-0 rounded-none border-[#E8E1D5]"
                        data-testid={`kyc-field-down-${b}-${idx}`}>
                        <ArrowDown size={12} />
                      </Button>
                      <span className="text-xs text-text-secondary flex-1 truncate">{field.label || <em className="text-text-secondary">(unnamed field)</em>}</span>
                      <Button onClick={() => removeField(idx)} variant="outline" size="sm"
                        className="h-7 px-2 rounded-none border-red-200 text-red-600 hover:bg-red-50"
                        data-testid={`kyc-field-remove-${b}-${idx}`}>
                        <Trash size={12} />
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] font-bold uppercase tracking-wider">Key (DB)</Label>
                        <Input value={field.key}
                          onChange={(e) => updateField(idx, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                          placeholder="contact_name" className="rounded-none border-[#E8E1D5] h-8 font-mono text-xs" />
                      </div>
                      <div className="space-y-1 sm:col-span-2">
                        <Label className="text-[10px] font-bold uppercase tracking-wider">Label (shown to user)</Label>
                        <Input value={field.label}
                          onChange={(e) => updateField(idx, { label: e.target.value })}
                          placeholder="Full name" className="rounded-none border-[#E8E1D5] h-8" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] font-bold uppercase tracking-wider">Type</Label>
                        <Select value={field.type} onValueChange={(v) => updateField(idx, { type: v })}>
                          <SelectTrigger className="rounded-none border-[#E8E1D5] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {fieldTypes.map(t => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-bold uppercase tracking-wider">Required?</Label>
                        <Select value={field.required ? 'yes' : 'no'}
                          onValueChange={(v) => updateField(idx, { required: v === 'yes' })}>
                          <SelectTrigger className="rounded-none border-[#E8E1D5] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No (optional)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-bold uppercase tracking-wider">Group</Label>
                        <Input value={field.group || ''}
                          onChange={(e) => updateField(idx, { group: e.target.value })}
                          placeholder="Identity / Documents / Location" className="rounded-none border-[#E8E1D5] h-8 text-xs" />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-wider">Placeholder</Label>
                      <Input value={field.placeholder || ''}
                        onChange={(e) => updateField(idx, { placeholder: e.target.value })}
                        placeholder="Hint text inside the input" className="rounded-none border-[#E8E1D5] h-8 text-xs" />
                    </div>

                    {(field.type === 'dropdown' || field.type === 'upload') && (
                      <div className="space-y-1">
                        <Label className="text-[10px] font-bold uppercase tracking-wider">
                          Options (comma-separated)
                        </Label>
                        <Input value={optionsAsCsv[idx] || ''}
                          onChange={(e) => updateField(idx, { options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                          placeholder="Aadhaar, PAN, Voter ID, Passport"
                          className="rounded-none border-[#E8E1D5] h-8 text-xs" />
                      </div>
                    )}

                    {field.type === 'upload' && (
                      <div className="space-y-1">
                        <Label className="text-[10px] font-bold uppercase tracking-wider">Accept (MIME types)</Label>
                        <Input value={field.accept || ''}
                          onChange={(e) => updateField(idx, { accept: e.target.value })}
                          placeholder="image/*,application/pdf" className="rounded-none border-[#E8E1D5] h-8 text-xs font-mono" />
                      </div>
                    )}

                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-wider">Help text (under the field)</Label>
                      <Textarea value={field.help || ''}
                        onChange={(e) => updateField(idx, { help: e.target.value })}
                        placeholder="Optional — explain what the user should provide"
                        className="rounded-none border-[#E8E1D5] text-xs" rows={2} />
                    </div>
                  </div>
                ))}

                <div className="flex flex-wrap gap-2 pt-2">
                  <Button onClick={addField} variant="outline"
                    className="rounded-none border-brand-primary text-brand-primary hover:bg-brand-accent/10"
                    data-testid={`kyc-add-field-${b}`}>
                    <Plus size={14} className="mr-2" /> Add field
                  </Button>
                  <Button onClick={saveBucket} disabled={saving}
                    className="bg-brand-primary text-sand-50 rounded-none disabled:opacity-50 ml-auto"
                    data-testid={`kyc-save-${b}`}>
                    {saving
                      ? <><Spinner size={14} className="mr-2 animate-spin" /> Saving…</>
                      : <><FloppyDisk size={14} className="mr-2" weight="fill" /> Save {BUCKET_LABELS[b]} form</>}
                  </Button>
                  <Button onClick={resetBucket} disabled={saving} variant="outline"
                    className="rounded-none border-text-secondary text-text-secondary"
                    data-testid={`kyc-reset-${b}`}>
                    <ArrowCounterClockwise size={14} className="mr-2" /> Reset to defaults
                  </Button>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminKycFormBuilderCard;
